import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
} from '../../../settings/store/services/details';
import {
  EditPSPAuthorizationFormValues,
  PSPAuthorizationKeys,
} from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';

const useAuthorization = () => {
  const {
    settings: {
      details: { validators, pspDetails },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPAuthorizationFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm<EditPSPAuthorizationFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty =
    !isEqual(fields, defaultFormValues) || Boolean(Object.keys(errors).length);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPAuthorizationKeys;
    }[] = [];

    if (
      values.publicRegistrationEnabled !==
      defaultFormValues?.publicRegistrationEnabled
    ) {
      updatedSettings.push({
        publicRegistrationEnabled: values.publicRegistrationEnabled,
        key: PSPAuthorizationKeys.PUBLIC_REGISTRATION_ENABLED,
      });
    }

    if (values.turnstileEnabled !== defaultFormValues?.turnstileEnabled) {
      updatedSettings.push({
        turnstileEnabled: values.turnstileEnabled,
        key: PSPAuthorizationKeys.TURNSTILE_ENABLED,
      });
    }

    if (values.disableAutoLocks !== defaultFormValues?.disableAutoLocks) {
      updatedSettings.push({
        disableAutoLocks: values.disableAutoLocks,
        key: PSPAuthorizationKeys.DISABLE_AUTO_LOCKS,
      });
    }

    if (
      values.disableTwilioAutoThrottle !==
      defaultFormValues?.disableTwilioAutoThrottle
    ) {
      updatedSettings.push({
        disableTwilioAutoThrottle: values.disableTwilioAutoThrottle,
        key: PSPAuthorizationKeys.DISABLE_TWILIO_AUTO_THROTTLE,
      });
    }

    // if (
    //   values.turnstileForceEnabled !== defaultFormValues?.turnstileForceEnabled
    // ) {
    //   updatedSettings.push({
    //     turnstileForceEnabled: values.turnstileForceEnabled,
    //     key: PSPAuthorizationKeys.TURNSTILE_FORCE_ENABLED,
    //   });
    // }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));
      }

      setDefaultFormValues({
        publicRegistrationEnabled: values?.publicRegistrationEnabled || false,
        turnstileEnabled: values?.turnstileEnabled || false,
        disableAutoLocks: values?.disableAutoLocks || false,
        disableTwilioAutoThrottle: values?.disableTwilioAutoThrottle || false,
        // turnstileForceEnabled: values?.turnstileForceEnabled || false,
      });
    }
  });

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const publicRegistrationEnabled = pspDetails?.find(
        (item) => item.key === 'PUBLIC-REGISTRATION-ENABLED',
      );
      const turnstileEnabled = pspDetails?.find(
        (item) => item.key === 'TURNSTILE-ENABLED',
      );

      const disableAutoLocks = pspDetails?.find(
        (item) => item.key === 'DISABLE-AUTO-LOCKS',
      );
      const disableTwilioAutoThrottle = pspDetails?.find(
        (item) => item.key === 'DISABLE-TWILIO-AUTO-THROTTLE',
      );
      // const turnstileForceEnabled = pspDetails?.find(
      //   (item) => item.key === 'TURNSTILE-FORCE-ENABLED',
      // );

      reset({
        publicRegistrationEnabled:
          (publicRegistrationEnabled?.data.value as boolean) || false,
        turnstileEnabled: (turnstileEnabled?.data.value as boolean) || false,
        disableAutoLocks: (disableAutoLocks?.data.value as boolean) || false,
        disableTwilioAutoThrottle:
          (disableTwilioAutoThrottle?.data.value as boolean) || false,
        // turnstileForceEnabled:
        //   (turnstileForceEnabled?.data.value as boolean) || false,
      });

      setDefaultFormValues({
        publicRegistrationEnabled:
          (publicRegistrationEnabled?.data.value as boolean) || false,
        turnstileEnabled: (turnstileEnabled?.data.value as boolean) || false,
        disableAutoLocks: (disableAutoLocks?.data.value as boolean) || false,
        disableTwilioAutoThrottle:
          (disableTwilioAutoThrottle?.data.value as boolean) || false,
        // turnstileForceEnabled:
        //   (turnstileForceEnabled?.data.value as boolean) || false,
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  return {
    isLoading,
    errorList,
    control,
    onSubmit,
    globalError,
    editIsLoading,
    isDirty,
  };
};

export default useAuthorization;
